import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { LoaderComponent } from '../../atoms/loader/loader.component';

export type AvatarVariant =
  | 'dog'
  | 'cat'
  | 'vet1'
  | 'vet2'
  | 'brown-skin-dark-hair-female'
  | 'brown-skin-dark-hair-female2'
  | 'brown-skin-dark-hair-male'
  | 'dark-skin-dark-hair-female'
  | 'dark-skin-dark-hair-female2'
  | 'dark-skin-dark-hair-male'
  | 'light-skin-brown-hair-female'
  | 'light-skin-brown-hair-female2'
  | 'light-skin-brown-hair-male'
  | 'light-skin-light-hair-female'
  | 'light-skin-light-hair-female2'
  | 'light-skin-light-hair-male'
  | 'gender-neutral-vet';
export type AvatarSize = 'sm' | 'md' | 'lg' | string;

const PREDEFINED_AVATAR_SIZES = ['sm', 'md', 'lg'] as const;
type PredefinedAvatarSize = (typeof PREDEFINED_AVATAR_SIZES)[number];

const PREDEFINED_AVATAR_SIZE_PX: Record<PredefinedAvatarSize, number> = {
  sm: 24,
  md: 32,
  lg: 48,
};

const LOADER_PADDING_RATIO = 0.25;
const DEFAULT_LOADER_SIZE = 48;
const AVATAR_LOADER_DURATION = 1600;

@Component({
  selector: 'joii-avatar',
  imports: [LoaderComponent, NgTemplateOutlet],
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-size]': 'size()',
    '[attr.data-loading]': 'loading() ? "" : null',
    '[style.width]': 'hostSize()',
    '[style.height]': 'hostSize()',
    '[style.--avatar-size]': 'avatarSizeStyle()',
  },
})
export class AvatarComponent {
  customSize = computed(() => {
    const s = this.size();
    return PREDEFINED_AVATAR_SIZES.includes(s as PredefinedAvatarSize) ? null : s;
  });
  avatarSizePx = computed(() => {
    const s = this.size();
    if (PREDEFINED_AVATAR_SIZES.includes(s as PredefinedAvatarSize)) {
      return PREDEFINED_AVATAR_SIZE_PX[s as PredefinedAvatarSize];
    }

    const pixelSize = /^(\d+(?:\.\d+)?)px$/.exec(s.trim());
    return pixelSize ? Number(pixelSize[1]) : null;
  });
  avatarSizeStyle = computed(() => {
    const avatarSize = this.avatarSizePx();
    return avatarSize !== null ? `${avatarSize}px` : this.customSize();
  });
  loaderSize = computed(() => {
    const avatarSize = this.avatarSizePx();
    return avatarSize !== null ? avatarSize + Math.round(avatarSize * LOADER_PADDING_RATIO) : DEFAULT_LOADER_SIZE;
  });
  hostSize = computed(() => {
    if (!this.loading()) {
      return this.customSize();
    }

    const avatarSize = this.avatarSizePx();
    return avatarSize !== null ? `${this.loaderSize()}px` : this.customSize();
  });
  imageUrl = computed(() => {
    if (this.src()) {
      return this.src();
    }
    switch (this.variant().toLowerCase()) {
      case 'dog':
        return 'design-system/large-dog.svg';
      case 'cat':
        return 'design-system/catProfile.svg';
      case 'vet1':
        return 'design-system/vet-avatar.png';
      case 'vet2':
        return 'design-system/vet-avatar-2.png';
      case 'brown-skin-dark-hair-female':
        return 'design-system/Brown skin_dark hair_female.png';
      case 'brown-skin-dark-hair-female2':
        return 'design-system/Brown skin_dark hair_female2.png';
      case 'brown-skin-dark-hair-male':
        return 'design-system/Brown skin_dark hair_male.png';
      case 'dark-skin-dark-hair-female':
        return 'design-system/Dark skin_dark hair_female.png';
      case 'dark-skin-dark-hair-female2':
        return 'design-system/Dark skin_dark hair_female2.png';
      case 'dark-skin-dark-hair-male':
        return 'design-system/Dark skin_dark hair_male.png';
      case 'light-skin-brown-hair-female':
        return 'design-system/Light skin_brown hair_female.png';
      case 'light-skin-brown-hair-female2':
        return 'design-system/Light skin_brown hair_female2.png';
      case 'light-skin-brown-hair-male':
        return 'design-system/Light skin_brown hair_male.png';
      case 'light-skin-light-hair-female':
        return 'design-system/Light skin_light hair_female.png';
      case 'light-skin-light-hair-female2':
        return 'design-system/Light skin_light hair_female2.png';
      case 'light-skin-light-hair-male':
        return 'design-system/Light skin_light hair_male.png';
      case 'gender-neutral-vet':
        return 'design-system/Gender_neutral_vet.png';
    }
    return '';
  });
  src = input<string>('');
  variant = input<AvatarVariant | string>('dog');
  size = input<AvatarSize>('md');
  loading = input<boolean>(false);
  loadingAnimationDuration = input<number>(AVATAR_LOADER_DURATION);
}
